import { Link } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { heroSlides } from "@/mocks/heroSlides";
import { newsList } from "@/mocks/news";
import { movieCategories } from "@/mocks/categories";
import { formatViews, getCategoryName, sortMovies } from "@/utils/catalog";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/feature/MovieCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";

export default function Home() {
  const heroMovies = heroSlides
    .map((slide) => ({
      backdrop: slide.backdrop,
      movie: movies.find((movie) => movie.id === slide.movieId),
    }))
    .filter((slide): slide is { backdrop: string; movie: (typeof movies)[number] } => Boolean(slide.movie));
  const latestMovies = sortMovies(movies, "latest").slice(0, 12);
  const hotRanking = sortMovies(movies, "hot").slice(0, 8);
  const latestNews = newsList.slice(0, 5);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <HeroCarousel movies={heroMovies} />

        {/* 分类快捷入口 */}
        <section className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <div className="mb-6 flex items-end justify-between">
            <div>
              <h2 className="text-xl font-bold text-foreground-50 md:text-2xl">电影分类</h2>
              <p className="mt-1 text-sm text-foreground-400">按类型快速找到你想看的电影</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-9 lg:gap-4">
            {movieCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group flex flex-col items-center gap-2 rounded-lg bg-background-100 p-4 ring-1 ring-background-200/70 transition hover:ring-accent-500/60"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-accent-500 transition group-hover:bg-accent-500 group-hover:text-background-950">
                  <i className={`${cat.icon} text-2xl`} />
                </span>
                <span className="text-sm font-medium text-foreground-100 group-hover:text-accent-400">
                  {cat.name}电影
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* 最新电影 + 热门排行 */}
        <section className="mx-auto w-full max-w-[1280px] px-4 pb-10 md:px-6 md:pb-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="mb-5 flex items-end justify-between">
                <div>
                  <h2 className="text-xl font-bold text-foreground-50 md:text-2xl">最新上映</h2>
                  <p className="mt-1 text-sm text-foreground-400">近期上线的高清电影推荐</p>
                </div>
                <Link
                  to="/category/action"
                  className="flex items-center gap-1 whitespace-nowrap text-sm text-accent-500 hover:text-accent-400"
                >
                  查看更多
                  <i className="ri-arrow-right-s-line" />
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
                {latestMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="mb-5 flex items-end justify-between">
                <h2 className="text-xl font-bold text-foreground-50 md:text-2xl">热门排行</h2>
                <span className="flex items-center gap-1 text-sm text-foreground-400">
                  <i className="ri-fire-fill text-accent-500" />
                  点击量榜单
                </span>
              </div>
              <ol className="overflow-hidden rounded-lg bg-background-100 ring-1 ring-background-200/70">
                {hotRanking.map((movie, index) => (
                  <li key={movie.id} className="border-b border-background-200/70 last:border-b-0">
                    <Link
                      to={`/detail/${movie.id}`}
                      className="flex items-center gap-3 p-3 transition hover:bg-background-200/60"
                    >
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                          index < 3
                            ? "bg-accent-500 text-background-950"
                            : "bg-background-200 text-foreground-300"
                        }`}
                      >
                        {index + 1}
                      </span>
                      <div className="h-16 w-11 shrink-0 overflow-hidden rounded-md bg-background-200">
                        <img
                          src={movie.poster}
                          alt={`${movie.title}海报`}
                          title={`${movie.title} - 电影天堂`}
                          className="h-full w-full object-top"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground-100">
                          {movie.title}
                        </p>
                        <p className="mt-1 truncate text-xs text-foreground-500">
                          {movie.genres.map(getCategoryName).join(" · ")} · {movie.year}
                        </p>
                        <p className="mt-1 flex items-center gap-1 text-xs text-accent-500">
                          <i className="ri-fire-line" />
                          {formatViews(movie.views)}
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ol>
            </aside>
          </div>
        </section>

        {/* 资讯快讯 */}
        <section className="border-y border-background-200/70 bg-background-100/60">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
            <div className="mb-6 flex items-end justify-between">
              <div>
                <h2 className="text-xl font-bold text-foreground-50 md:text-2xl">电影资讯快讯</h2>
                <p className="mt-1 text-sm text-foreground-400">行业动态、票房榜单与幕后观察</p>
              </div>
              <Link
                to="/news"
                className="flex items-center gap-1 whitespace-nowrap text-sm text-accent-500 hover:text-accent-400"
              >
                全部资讯
                <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {latestNews.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="group flex items-start gap-3 rounded-lg bg-background-100 p-4 ring-1 ring-background-200/70 transition hover:ring-accent-500/60"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100/20 text-accent-500">
                    <i className="ri-newspaper-line text-xl" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="line-clamp-1 text-sm font-medium text-foreground-100 transition group-hover:text-accent-400 md:text-base">
                      {item.title}
                    </h3>
                    <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-400">
                      {item.summary}
                    </p>
                    <p className="mt-2 text-xs text-foreground-500">{item.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* 网站简介 */}
        <section className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 items-center gap-8 rounded-lg bg-primary-950 p-6 md:grid-cols-2 md:p-10">
            <div>
              <h2 className="text-2xl font-bold text-foreground-50 md:text-3xl">
                关于电影天堂
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-300 md:text-base">
                电影天堂专注于最新电影推荐与影视资源内容分享，汇聚动作、科幻、剧情、爱情等各类电影，
                为每一位电影爱好者提供便捷的查找、浏览与了解最新电影信息的体验。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-300 md:text-base">
                我们不提供任何下载与播放服务，仅作为影视内容的展示与推荐平台，所有内容均为演示用模拟数据。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/about"
                  className="whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-950 transition hover:bg-accent-400"
                >
                  了解更多
                </Link>
                <Link
                  to="/news"
                  className="whitespace-nowrap rounded-md border border-foreground-100/25 px-5 py-2.5 text-sm text-foreground-50 transition hover:border-accent-500/70 hover:text-accent-400"
                >
                  查看资讯
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {[
                { value: "5000+", label: "收录影片" },
                { value: "9 大", label: "电影分类" },
                { value: "每日", label: "更新推荐" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-lg bg-primary-900/70 p-4 text-center"
                >
                  <p className="font-display text-2xl text-accent-400 md:text-3xl">{stat.value}</p>
                  <p className="mt-1 text-xs text-foreground-300 md:text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}